/**
 * Dashboard Controller
 * Manages the main dashboard interface
 */

// Import dependent classes helper (assumed loaded via script tags)
// FolderUploader, OpenFOAMLoader, Utils classes are available globally

class Dashboard {
    constructor() {
        this.grid = document.getElementById('simulationsGrid');
        this.breadcrumb = document.getElementById('dashboardBreadcrumb');
        this.filter = 'owned'; // 'owned' (My Simulations) or 'shared-with-me'
        this.currentPath = null; // null = root
        this.folderDisplayNames = {}; // Map internal names to display names
        this.openfoamLoader = new OpenFOAMLoader(this);
        this.init();
    }

    async init() {
        console.log('Dashboard initialized');

        // Check Auth
        if (!window.Auth.getToken()) {
            console.log("No token found, redirecting to landing");
            window.location.href = 'landing.html';
            return;
        }

        // Setup User Info - always fetch fresh to check company admin status
        await this.loadUserInfo();

        // Update Storage Stats
        this.updateStorageDisplay();

        // Load Content
        this.loadContent();

        // Setup Navigation - only for nav items with data-view attribute
        // (not for actual links like Account & Billing)
        document.querySelectorAll('.nav-item[data-view]').forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                document.querySelectorAll('.nav-item').forEach(i => i.classList.remove('active'));
                item.classList.add('active');

                // Update filter
                const view = item.dataset.view;
                if (view === 'my-simulations') this.filter = 'owned';
                else if (view === 'shared-with-me') this.filter = 'shared-with-me';
                else if (view === 'recent') this.filter = 'recent';
                else if (view === 'my-designs') this.filter = 'my-designs';

                // Update view title
                const viewTitle = document.getElementById('viewTitle');
                if (viewTitle) {
                    if (view === 'my-simulations') viewTitle.textContent = 'My Simulations';
                    else if (view === 'shared-with-me') viewTitle.textContent = 'Shared With Me';
                    else if (view === 'recent') viewTitle.textContent = 'Recent';
                    else if (view === 'my-designs') viewTitle.textContent = 'My Designs';
                }

                // Clear search and update placeholder
                this.clearSearch();
                if (this.updateSearchPlaceholder) {
                    this.updateSearchPlaceholder();
                }

                // Load appropriate content based on view
                if (view === 'my-designs') {
                    this.loadDesignsView();
                } else {
                    this.loadContent();
                }
            });
        });

        // Setup "New Simulation" Dropdown
        this.setupNewSimulationDropdown();

        // Setup "Design" Dropdown (Admin Only)
        this.setupDesignDropdown();

        // Logout
        document.getElementById('btnLogout').addEventListener('click', () => {
            window.Auth.logout();
        });

        // Search
        this.setupSearch();

        // Rename Modal Event Listeners
        this.setupRenameModal();

        // Delete Modal Event Listeners
        this.setupDeleteModal();

        // Share Modal - use shared ShareModal component
        if (window.shareModal) {
            window.shareModal.init();
        }
    }

    async loadUserInfo() {
        try {
            const res = await window.Auth.authenticatedFetch('/api/auth/me');
            const data = await res.json();
            if (data.status === 'success') {
                document.getElementById('userNameDisplay').textContent = data.data.user.name;

                // Show Company Admin button if user is a company admin
                if (data.data.user.is_company_admin && data.data.user.company_admin_info) {
                    this.showCompanyAdminButton(data.data.user.company_admin_info);
                }

                // Show "My Designs" nav if user is admin
                if (data.data.user.is_admin) {
                    const navMyDesigns = document.getElementById('navMyDesigns');
                    if (navMyDesigns) {
                        navMyDesigns.style.display = 'flex';
                    }
                }
            }
        } catch (e) { console.error(e); }
    }

    showCompanyAdminButton(companyInfo) {
        // Add Company Admin button to user profile section
        const userProfile = document.querySelector('.user-profile');
        if (!userProfile) return;

        // Check if button already exists
        if (document.getElementById('btnCompanyAdmin')) return;

        const companyAdminBtn = document.createElement('a');
        companyAdminBtn.id = 'btnCompanyAdmin';
        companyAdminBtn.href = '/company-admin.html';
        companyAdminBtn.className = 'company-admin-btn';
        companyAdminBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <span>Company Admin</span>
        `;
        companyAdminBtn.title = `Manage ${companyInfo.company_name}`;

        // Insert before the logout button
        const logoutBtn = document.getElementById('btnLogout');
        if (logoutBtn) {
            userProfile.insertBefore(companyAdminBtn, logoutBtn);
        } else {
            userProfile.appendChild(companyAdminBtn);
        }
    }

    setupNewSimulationDropdown() {
        const dropdown = document.getElementById('newSimulationDropdown');
        const btnToggle = document.getElementById('btnNewSimulation');
        const btnUploadFolder = document.getElementById('btnUploadFolder');
        const btnUploadFile = document.getElementById('btnUploadFile');

        if (!dropdown || !btnToggle) return;

        // Create hidden folder input
        let folderInput = document.getElementById('dashboardFolderInput');
        if (!folderInput) {
            folderInput = document.createElement('input');
            folderInput.type = 'file';
            folderInput.id = 'dashboardFolderInput';
            folderInput.webkitdirectory = true;
            folderInput.directory = true;
            folderInput.multiple = true;
            folderInput.style.display = 'none';
            document.body.appendChild(folderInput);
            folderInput.addEventListener('change', (e) => this.handleFolderUpload(e));
        }

        // Create hidden file input for single files (STL, OBJ, VTK, etc.)
        let fileInput = document.getElementById('dashboardFileInput');
        if (!fileInput) {
            fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.id = 'dashboardFileInput';
            fileInput.accept = '.stl,.obj,.vtk,.vtu,.vtp,.vts,.vti,.vtm,.ply,.json,.case,.encas,.cas,.geo,.step,.stp,.iges,.igs,.nc,.cdf,.xyz,.p3d,.cgns,.exo,.h5,.xdmf,.plt';
            fileInput.style.display = 'none';
            document.body.appendChild(fileInput);
            fileInput.addEventListener('change', (e) => this.handleSingleFileUpload(e));
        }

        // Toggle dropdown on button click
        btnToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.classList.toggle('open');
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target)) {
                dropdown.classList.remove('open');
            }
        });

        // Handle folder upload option
        if (btnUploadFolder) {
            btnUploadFolder.addEventListener('click', () => {
                dropdown.classList.remove('open');
                folderInput.click();
            });
        }

        // Handle single file upload option
        if (btnUploadFile) {
            btnUploadFile.addEventListener('click', () => {
                dropdown.classList.remove('open');
                fileInput.click();
            });
        }
    }

    setupDesignDropdown() {
        const dropdown = document.getElementById('designDropdown');
        const btnToggle = document.getElementById('btnDesign');
        const btnNewDesign = document.getElementById('btnNewDesign');
        const btnImportSTEP = document.getElementById('btnImportSTEP');
        const btnBrowseDesigns = document.getElementById('btnBrowseDesigns');

        if (!dropdown || !btnToggle) {
            console.log('[CAD] Design dropdown elements not found');
            return;
        }

        // Check if user is admin
        const userInfo = window.Auth ? window.Auth.getUserInfo() : null;
        const isAdmin = userInfo?.is_admin === true;

        console.log('[CAD] User info:', userInfo);
        console.log('[CAD] Is admin:', isAdmin);

        // Only show Design button for admin users
        if (!isAdmin) {
            console.log('[CAD] User is not admin, hiding Design button');
            dropdown.style.display = 'none';
            return;
        }

        console.log('[CAD] User is admin, showing Design button');
        dropdown.style.display = 'flex';

        // Toggle dropdown on button click
        btnToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.classList.toggle('open');
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target)) {
                dropdown.classList.remove('open');
            }
        });

        // Handle "New Design" - Create new CAD document and open viewer
        if (btnNewDesign) {
            btnNewDesign.addEventListener('click', async () => {
                dropdown.classList.remove('open');

                try {
                    // Create new CAD document via API
                    const token = window.Auth.getToken();
                    const response = await fetch('/api/cad/documents', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            name: `Design ${new Date().toISOString().split('T')[0]}`,
                            description: 'Created from dashboard'
                        })
                    });

                    if (!response.ok) {
                        throw new Error('Failed to create design');
                    }

                    const data = await response.json();

                    // Open viewer in CAD mode
                    window.location.href = `index.html?mode=cad&document=${data.id}`;

                } catch (error) {
                    console.error('Failed to create design:', error);
                    Utils.notify('Failed to create design: ' + error.message, 'error');
                }
            });
        }

        // Handle "Import STEP File"
        if (btnImportSTEP) {
            btnImportSTEP.addEventListener('click', () => {
                dropdown.classList.remove('open');

                // Create hidden file input for STEP files
                let stepInput = document.getElementById('dashboardStepInput');
                if (!stepInput) {
                    stepInput = document.createElement('input');
                    stepInput.type = 'file';
                    stepInput.id = 'dashboardStepInput';
                    stepInput.accept = '.step,.stp,.iges,.igs';
                    stepInput.style.display = 'none';
                    document.body.appendChild(stepInput);

                    stepInput.addEventListener('change', async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        try {
                            const formData = new FormData();
                            formData.append('file', file);

                            const token = window.Auth.getToken();
                            Utils.notify('Importing STEP file...', 'info');

                            const response = await fetch('/api/cad/import/step', {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                },
                                body: formData
                            });

                            if (!response.ok) {
                                throw new Error('Import failed');
                            }

                            const data = await response.json();
                            Utils.notify('STEP file imported successfully', 'success');

                            // Open in CAD viewer
                            window.location.href = `index.html?mode=cad&document=${data.document_id}`;

                        } catch (error) {
                            console.error('STEP import failed:', error);
                            Utils.notify('Failed to import STEP file: ' + error.message, 'error');
                        }

                        e.target.value = '';
                    });
                }

                stepInput.click();
            });
        }

        // Handle "Browse My Designs" - Show list of CAD documents
        if (btnBrowseDesigns) {
            btnBrowseDesigns.addEventListener('click', async () => {
                dropdown.classList.remove('open');

                try {
                    const token = window.Auth.getToken();
                    const response = await fetch('/api/cad/documents', {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error('Failed to load designs');
                    }

                    const data = await response.json();

                    if (data.documents && data.documents.length > 0) {
                        // Show modal with list of designs
                        this.showDesignListModal(data.documents);
                    } else {
                        Utils.notify('No designs found. Create your first design!', 'info');
                    }

                } catch (error) {
                    console.error('Failed to load designs:', error);
                    Utils.notify('Failed to load designs: ' + error.message, 'error');
                }
            });
        }
    }

    showDesignListModal(designs) {
        // Create modal to show list of designs
        // For now, just show the first one
        if (designs.length > 0) {
            const firstDesign = designs[0];
            if (confirm(`Open design: ${firstDesign.name}?`)) {
                window.location.href = `index.html?mode=cad&document=${firstDesign.id}`;
            }
        }
    }

    async handleSingleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        // Reset input so same file can be selected again
        e.target.value = '';

        const fileName = file.name;
        const fileSize = file.size;

        // Create a FolderUploader instance for progress display
        const uploader = new FolderUploader(this);

        // Chunked upload settings (same as folder uploader)
        const CHUNK_SIZE = 20 * 1024 * 1024; // 20 MB chunks
        const PARALLEL_CONNECTIONS = 6;
        const LARGE_FILE_THRESHOLD = 20 * 1024 * 1024; // Use chunked upload for files > 20 MB

        try {
            uploader.showProgress(0, `Uploading ${fileName}...`);
            const token = window.Auth.getToken();
            const startTime = performance.now();

            let result;

            if (fileSize <= LARGE_FILE_THRESHOLD) {
                // Small file: single request with XHR progress
                result = await this._uploadSingleRequest(file, token, uploader);
            } else {
                // Large file: parallel chunked upload
                result = await this._uploadChunked(file, token, uploader, CHUNK_SIZE, PARALLEL_CONNECTIONS);
            }

            const totalTime = ((performance.now() - startTime) / 1000).toFixed(1);
            const speed = (fileSize / 1024 / 1024) / (totalTime || 1);
            console.log(`Upload complete: ${this.formatBytes(fileSize)} in ${totalTime}s (${speed.toFixed(1)} MB/s)`);

            // Show 100% complete
            uploader.showProgress(100, 'Upload complete!');
            await new Promise(resolve => setTimeout(resolve, 500));
            uploader.hideProgress();

            this.showNotification(`Uploaded ${fileName} successfully`, 'success');

            // Refresh the file list
            this.loadContent();
            this.updateStorageDisplay();

            // Optionally open the viewer with the uploaded file
            const fileId = result.data?.file_id;
            const casePath = result.case_path || result.data?.case_path;
            if (fileId || casePath) {
                const openNow = confirm(`File uploaded successfully!\n\nWould you like to open it in the viewer now?`);
                if (openNow) {
                    const caseRef = fileId || casePath;
                    window.location.href = `/?case=${encodeURIComponent(caseRef)}`;
                }
            }

        } catch (error) {
            console.error('Single file upload failed:', error);
            uploader.hideProgress();
            this.showNotification(`Upload failed: ${error.message}`, 'error');
        }
    }

    /**
     * Upload a small file in a single request with XHR progress tracking
     */
    _uploadSingleRequest(file, token, uploader) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    uploader.showProgress(Math.min(percent, 99), `${this.formatBytes(event.loaded)} / ${this.formatBytes(event.total)}`);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try { resolve(JSON.parse(xhr.responseText)); }
                    catch (e) { reject(new Error('Invalid response from server')); }
                } else {
                    try {
                        const error = JSON.parse(xhr.responseText);
                        reject(new Error(error.detail || `Upload failed (${xhr.status})`));
                    } catch (e) { reject(new Error(`Upload failed (${xhr.status})`)); }
                }
            });

            xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
            xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

            const formData = new FormData();
            formData.append('file', file);

            xhr.open('POST', '/api/upload-single-file');
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(formData);
        });
    }

    /**
     * Upload a large file using parallel chunked upload
     */
    async _uploadChunked(file, token, uploader, chunkSize, parallelConnections) {
        const totalChunks = Math.ceil(file.size / chunkSize);
        const uploadId = crypto.randomUUID().slice(0, 8);
        const chunkProgress = new Array(totalChunks).fill(0);

        console.log(`Chunked upload: ${file.name} → ${totalChunks} chunks of ${this.formatBytes(chunkSize)}, ${parallelConnections} parallel connections`);

        uploader.showProgress(0, `Splitting into ${totalChunks} chunks...`);

        const updateProgress = () => {
            const uploaded = chunkProgress.reduce((sum, p) => sum + p, 0);
            const percent = Math.min(99, Math.round((uploaded / file.size) * 100));
            uploader.showProgress(percent, `${this.formatBytes(uploaded)} / ${this.formatBytes(file.size)}`);
        };

        // Upload a single chunk via XHR with progress
        const uploadChunk = (chunkIndex) => {
            return new Promise((resolve, reject) => {
                const start = chunkIndex * chunkSize;
                const end = Math.min(start + chunkSize, file.size);
                const chunkBlob = file.slice(start, end);

                const formData = new FormData();
                formData.append('chunk', chunkBlob, `chunk_${chunkIndex}`);
                formData.append('file_name', file.name);
                formData.append('chunk_index', chunkIndex);
                formData.append('total_chunks', totalChunks);
                formData.append('file_size', file.size);
                formData.append('upload_id', uploadId);

                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        chunkProgress[chunkIndex] = e.loaded;
                        updateProgress();
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        chunkProgress[chunkIndex] = end - start; // Mark complete
                        updateProgress();
                        try { resolve(JSON.parse(xhr.responseText)); }
                        catch (e) { reject(new Error('Invalid response')); }
                    } else {
                        try {
                            const error = JSON.parse(xhr.responseText);
                            reject(new Error(error.detail || `Chunk ${chunkIndex} failed (${xhr.status})`));
                        } catch (e) { reject(new Error(`Chunk ${chunkIndex} failed (${xhr.status})`)); }
                    }
                });

                xhr.addEventListener('error', () => reject(new Error(`Network error on chunk ${chunkIndex}`)));
                xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

                xhr.open('POST', '/api/upload-single-file-chunked');
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(formData);
            });
        };

        // Upload all chunks with parallel sliding window
        let lastCompleteResult = null;

        await new Promise((resolveAll, rejectAll) => {
            let nextChunkIndex = 0;
            let activeCount = 0;
            let rejected = false;

            const startNext = () => {
                if (rejected) return;

                while (activeCount < parallelConnections && nextChunkIndex < totalChunks) {
                    const idx = nextChunkIndex++;
                    activeCount++;

                    uploadChunk(idx)
                        .then(result => {
                            if (rejected) { activeCount--; return; }
                            if (result.status === 'complete') {
                                lastCompleteResult = result;
                            }
                            activeCount--;
                            if (nextChunkIndex < totalChunks) {
                                startNext();
                            } else if (activeCount === 0 && !rejected) {
                                resolveAll();
                            }
                        })
                        .catch(err => {
                            activeCount--;
                            if (!rejected) {
                                rejected = true;
                                rejectAll(err);
                            }
                        });
                }

                if (nextChunkIndex >= totalChunks && activeCount === 0 && !rejected) {
                    resolveAll();
                }
            };

            startNext();
        });

        // The last chunk's response should have the complete result
        if (!lastCompleteResult) {
            throw new Error('Upload completed but no assembly confirmation received');
        }

        return lastCompleteResult;
    }

    // Build a case reference string using file_id (UUID) when available
    buildCaseRef(item) {
        if (item.file_id) {
            const sub = item.file_id_sub_path || '';
            if (sub) {
                const cleanSub = sub.startsWith('/') ? sub.substring(1) : sub;
                return `${item.file_id}/${cleanSub}`;
            }
            return item.file_id;
        }
        return item.path || item.id;
    }

    // Helper to format bytes
    formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    setupSearch() {
        const searchInput = document.getElementById('searchInput');
        const searchContainer = document.getElementById('searchContainer');
        const searchClear = document.getElementById('searchClear');

        if (!searchInput) return;

        this.searchTimeout = null;
        this.isSearching = false;

        // Update placeholder based on current view
        this.updateSearchPlaceholder = () => {
            if (this.filter === 'shared-with-me') {
                searchInput.placeholder = 'Search by name, sharer, or email...';
            } else {
                searchInput.placeholder = 'Search simulations...';
            }
        };

        // Debounced search
        searchInput.addEventListener('input', () => {
            const query = searchInput.value.trim();

            // Toggle clear button
            if (query.length > 0) {
                searchContainer.classList.add('has-value');
            } else {
                searchContainer.classList.remove('has-value');
            }

            // Clear previous timeout
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            // If empty, restore normal view
            if (query.length === 0) {
                this.isSearching = false;
                this.loadContent();
                return;
            }

            // Debounce search (wait 300ms after user stops typing)
            if (query.length >= 2) {
                this.searchTimeout = setTimeout(() => {
                    this.performSearch(query);
                }, 300);
            }
        });

        // Clear button
        if (searchClear) {
            searchClear.addEventListener('click', () => {
                searchInput.value = '';
                searchContainer.classList.remove('has-value');
                this.isSearching = false;
                this.loadContent();
            });
        }

        // Handle Escape key to clear search
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                searchInput.value = '';
                searchContainer.classList.remove('has-value');
                this.isSearching = false;
                searchInput.blur();
                this.loadContent();
            }
        });
    }

    clearSearch() {
        const searchInput = document.getElementById('searchInput');
        const searchContainer = document.getElementById('searchContainer');

        if (searchInput) {
            searchInput.value = '';
        }
        if (searchContainer) {
            searchContainer.classList.remove('has-value');
        }
        this.isSearching = false;

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    }

    async performSearch(query) {
        this.isSearching = true;
        this.grid.innerHTML = '<div class="loading-spinner">Searching...</div>';

        try {
            if (this.filter === 'shared-with-me') {
                // Search shared sessions
                await this.searchSharedSessions(query);
            } else {
                // Search owned files
                await this.searchMySimulations(query);
            }
        } catch (e) {
            console.error('Search error:', e);
            this.grid.innerHTML = `<div class="error">Search failed: ${e.message}</div>`;
        }
    }

    async searchMySimulations(query) {
        try {
            const res = await window.Auth.authenticatedFetch('/api/search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query })
            });

            if (!res.ok) throw new Error('Search failed');

            const result = await res.json();
            const items = result.items || [];

            // Hide breadcrumb during search
            if (this.breadcrumb) {
                this.breadcrumb.style.display = 'none';
            }

            // Transform to card format
            this.renderGrid(items.map(item => ({
                id: item.path,
                title: item.name,
                type: 'file',
                is_dir: item.is_dir,
                is_case: item.is_case,
                case_type: item.case_type,
                size: item.size || 0,
                updated_at: item.modified || Date.now(),
                is_shared: false,
                path: item.path,
                file_id: item.file_id || null,
                file_id_sub_path: item.file_id_sub_path || '',
                relativePath: item.relative_path || ''
            })));

            // Show search result info
            if (items.length === 0) {
                this.grid.innerHTML = `<div class="empty-state" style="grid-column: 1/-1;">No results found for "${query}"</div>`;
            }

        } catch (e) {
            console.error('Search my simulations error:', e);
            throw e;
        }
    }

    async searchSharedSessions(query) {
        try {
            const res = await window.Auth.authenticatedFetch(`/api/sessions/search?query=${encodeURIComponent(query)}`);

            if (!res.ok) throw new Error('Search failed');

            const data = await res.json();

            this.renderGrid(data.sessions.map(s => ({
                id: s.id,
                title: s.session_name,
                type: 'session',
                updated_at: s.shared_at || s.updated_at,
                is_shared: true,
                role: s.role,
                thumbnail_url: s.thumbnail_url,
                shared_by_name: s.shared_by_name,
                shared_by_email: s.shared_by_email,
                data_path: s.data_path,
                data_source: s.data_source,
                // Use backend's is_case detection (checks actual folder structure)
                is_case: s.is_case === true
            })));

            if (data.sessions.length === 0) {
                this.grid.innerHTML = `<div class="empty-state" style="grid-column: 1/-1;">No results found for "${query}"</div>`;
            }

        } catch (e) {
            console.error('Search shared sessions error:', e);
            throw e;
        }
    }

    async loadContent() {
        this.grid.innerHTML = '<div class="loading-spinner">Loading...</div>';
        // Reset to root when changing views
        this.currentPath = null;

        try {
            if (this.filter === 'owned' || this.filter === 'recent') {
                // Fetch User Files (Cases)
                await this.loadMySimulations();
            } else if (this.filter === 'shared-with-me') {
                // Fetch Shared Sessions
                await this.loadSharedSessions();
            }
        } catch (e) {
            console.error(e);
            this.grid.innerHTML = '<div class="error">Failed to load content.</div>';
        }
    }

    async loadMySimulations() {
        try {
            // Use browse to get root items
            // Assuming root browsing returns user's top level folders
            const result = await this.openfoamLoader.browseDirectory(null);

            if (result.status !== 'success') throw new Error('Failed to browse files');

            const items = result.items || [];

            // Store display names for breadcrumb (use internal folder name as key)
            items.forEach(item => {
                if (item.internal_name && item.name) {
                    this.folderDisplayNames[item.internal_name] = item.name;
                }
            });

            // Update breadcrumb for root - use relative_path from result
            this.currentRelativePath = result.relative_path || '';
            this.renderBreadcrumb(this.currentRelativePath, result);

            // Transform to card format - use relative_path for navigation
            this.renderGrid(items.map(item => ({
                id: item.path, // Use full path as ID for files
                title: item.name,
                type: 'file',
                is_dir: item.is_dir,
                is_case: item.is_case,
                case_type: item.case_type,
                size: item.size || 0, // Include size from API
                updated_at: item.modified || Date.now(),
                is_shared: false, // Files aren't shared via the session system yet
                icon: item.is_case ? '🌊' : '📁',
                path: item.path, // Full path for API calls
                file_id: item.file_id || null,
                file_id_sub_path: item.file_id_sub_path || '',
                relativePath: item.relative_path || '' // Relative path for breadcrumb
            })));

        } catch (e) {
            console.error("Load My Sims Error:", e);
            this.grid.innerHTML = `<div class="error">Failed to load simulations: ${e.message}</div>`;
        }
    }

    async loadSharedSessions() {
        try {
            // Hide breadcrumb for shared sessions view
            if (this.breadcrumb) {
                this.breadcrumb.style.display = 'none';
            }

            const res = await window.Auth.authenticatedFetch(`/api/sessions?filter=shared-with-me`);
            if (!res.ok) throw new Error(`Failed to load shared sessions: ${res.statusText} (${res.status})`);

            const data = await res.json();
            this.renderGrid(data.sessions.map(s => ({
                id: s.id,
                title: s.session_name,
                type: 'session',
                updated_at: s.shared_at || s.updated_at, // Use sharing date
                is_shared: true,
                role: s.role,
                thumbnail_url: s.thumbnail_url,
                shared_by_name: s.shared_by_name,
                shared_by_email: s.shared_by_email,
                data_path: s.data_path,
                data_source: s.data_source,
                // Use backend's is_case detection (checks actual folder structure)
                is_case: s.is_case === true
            })));
        } catch (e) {
            console.error("Load Shared Body Error:", e);
            this.grid.innerHTML = `<div class="error">Failed to load shared sessions: ${e.message}</div>`;
        }
    }

    renderGrid(items) {
        // Use file-list-grid class which is defined in main.css
        this.grid.className = 'file-list-grid';
        this.grid.innerHTML = '';

        if (items.length === 0) {
            this.grid.innerHTML = '<div class="empty-state" style="grid-column: 1/-1; text-align:center; padding: 40px; color: #888;">No simulations found.</div>';
            return;
        }

        items.forEach(item => {
            const el = document.createElement('div');
            // Classes from main.css
            let classes = ['file-item'];
            if (item.is_case) classes.push('openfoam-case');
            if (item.type === 'session') classes.push('openfoam-case'); // Style sessions like cases

            el.className = classes.join(' ');

            // Attributes matching controls.js patterns
            el.dataset.id = item.id;
            el.dataset.type = item.type;
            if (item.path) el.dataset.path = item.path;

            // Escape title for HTML attribute
            const escapedTitle = item.title ? item.title.replace(/"/g, '&quot;') : '';

            // Click handler
            el.onclick = () => {
                console.log('Click on item:', item);
                console.log('item.type:', item.type, 'item.is_case:', item.is_case, 'item.is_shared:', item.is_shared, 'item.sessionId:', item.sessionId);
                if (item.type === 'file' && item.is_case) {
                    // Check if this is shared content (inside a shared folder)
                    if (item.is_shared && item.sessionId) {
                        // Simulation inside a shared folder - use shared_case parameter
                        // This tells the backend the user has access via the parent session
                        const url = `index.html?shared_case=${encodeURIComponent(item.path)}&via_session=${item.sessionId}`;
                        console.log('Navigating to shared case URL:', url);
                        window.location.href = url;
                    } else {
                        // Own simulation - use file_id-based URL if available
                        const caseRef = this.buildCaseRef(item);
                        // Pass display name so viewer doesn't need to fetch it
                        const nameParam = item.title ? `&name=${encodeURIComponent(item.title)}` : '';
                        window.location.href = `index.html?case=${encodeURIComponent(caseRef)}${nameParam}`;
                    }
                } else if (item.type === 'session') {
                    // For shared sessions, check if it's a case or a folder
                    if (item.is_case) {
                        // It's a simulation case - open in viewer with session
                        window.location.href = `index.html?session=${item.id}`;
                    } else if (item.data_path) {
                        // It's a shared folder - browse into it
                        this.browseSharedFolder(item.data_path, item.id);
                    } else {
                        // Fallback - try to open as session
                        window.location.href = `index.html?session=${item.id}`;
                    }
                } else if (item.type === 'file' && item.is_dir) {
                    // Navigate into directory - check if it's shared content
                    if (item.is_shared && item.sessionId) {
                        // Browsing inside a shared folder
                        this.browseSharedFolder(item.path, item.sessionId);
                    } else {
                        // Own folder
                        this.browseFolder(item.path);
                    }
                } else if (item.type === 'file' && !item.is_dir && !item.is_case) {
                    // Standalone geometry file (STL, OBJ, STEP, PLY, VTK, etc.)
                    const ext = (item.title || '').split('.').pop().toLowerCase();
                    const geometryExts = ['stl', 'obj', 'vtk', 'vtu', 'vtp', 'ply', 'step', 'stp'];
                    if (geometryExts.includes(ext)) {
                        const caseRef = this.buildCaseRef(item);
                        // Pass display name so viewer doesn't need to fetch it
                        const nameParam = item.title ? `&name=${encodeURIComponent(item.title)}` : '';
                        window.location.href = `index.html?case=${encodeURIComponent(caseRef)}${nameParam}`;
                    }
                }
            };

            // Icon - use SVG icons
            let icon = '<img src="static/assets/icons/folder-case.svg" alt="" width="48" height="48" class="file-icon-img">'; // Default folder icon
            if (item.type === 'session') icon = '<img src="static/assets/icons/collaboration.svg" alt="" width="48" height="48" class="file-icon-img">'; // Shared session icon
            else if (item.is_case) {
                if (item.case_type === 'openfoam') icon = '<img src="static/assets/icons/openfoam.svg" alt="" width="48" height="48" class="file-icon-img">';
                else if (item.case_type === 'fluent') icon = '<img src="static/assets/icons/fluent.svg" alt="" width="48" height="48" class="file-icon-img">'; // Fluent icon
                else if (item.case_type === 'ensight') icon = '<img src="static/assets/icons/ensight.svg" alt="" width="48" height="48" class="file-icon-img">'; // EnSight icon
                else if (item.case_type === 'geometry') icon = '<img src="static/assets/icons/cube-3d.svg" alt="" width="48" height="48" class="file-icon-img">'; // Geometry file icon
                else icon = '<img src="static/assets/icons/cube-3d.svg" alt="" width="48" height="48" class="file-icon-img">'; // Generic case icon
            }

            // Badge
            let badgeHTML = '';
            let caseTypeLabel = 'OpenFOAM';
            if (item.case_type === 'fluent') caseTypeLabel = 'Fluent';
            if (item.case_type === 'ensight') caseTypeLabel = 'EnSight';
            if (item.case_type === 'geometry') caseTypeLabel = 'Geometry';

            if (item.type === 'session') {
                // Shared session (folder or case) - always show "Shared" badge
                badgeHTML = `<div class="case-badge" style="background:var(--apple-purple, #AF52DE)">Shared</div>`;
            } else if (item.is_case) {
                // Own simulation case - show case type badge with type-specific color
                let badgeColor = '#4CAF50'; // default green
                if (item.case_type === 'openfoam') badgeColor = '#2196F3';
                else if (item.case_type === 'ensight') badgeColor = '#FF9800';
                else if (item.case_type === 'fluent') badgeColor = '#F44336';
                else if (item.case_type === 'geometry') badgeColor = '#00BCD4';
                badgeHTML = `<div class="case-badge" style="background:${badgeColor}">${caseTypeLabel}</div>`;
            }

            // Determine if this is a shared item (shared with me, not owned by me)
            const isSharedWithMe = item.type === 'session' || (item.is_shared && item.sessionId);
            // Editors can download shared items, viewers cannot
            const canDownloadShared = isSharedWithMe && item.role === 'editor';

            // Format size (hide for shared sessions since we don't have that info)
            const sizeStr = isSharedWithMe ? '' : this.formatBytes(item.size || 0);

            // Parse date - handle both Unix timestamps (number) and ISO strings
            let dateObj;
            if (typeof item.updated_at === 'string') {
                dateObj = new Date(item.updated_at); // ISO string
            } else {
                dateObj = new Date(item.updated_at * 1000); // Unix timestamp
            }
            const dateStr = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleDateString();

            // Build action buttons based on ownership and role
            // Download button with square border progress indicator (rounded corners)
            // Rounded rect path: starts top-center, goes clockwise
            // Button is 28x28 with 6px border radius, stroke inset by 1px
            const downloadButtonHTML = `
                <button class="btn-action btn-download" title="Download" data-action="download">
                    <img src="static/assets/icons/download.svg" alt="" width="16" height="16" class="action-icon">
                    <div class="download-progress">
                        <svg viewBox="0 0 28 28">
                            <rect class="progress-bg" x="1" y="1" width="26" height="26" rx="5" ry="5"></rect>
                            <rect class="progress-ring" x="1" y="1" width="26" height="26" rx="5" ry="5"></rect>
                        </svg>
                        <span class="progress-text">0%</span>
                    </div>
                </button>
            `;

            let actionsHTML = '';
            // Mobile menu button (three dots)
            const mobileMenuBtn = `<button class="btn-card-menu" title="Actions" aria-label="Actions">⋮</button>`;

            if (!isSharedWithMe) {
                // Owned items: full control (download, share, rename, delete)
                // Share button is only shown for actual cases, not regular folders
                const shareButtonHTML = item.is_case
                    ? `<button class="btn-action btn-share" title="Share" data-action="share"><img src="static/assets/icons/share.svg" alt="" width="16" height="16" class="action-icon"></button>`
                    : '';
                actionsHTML = `
                    ${mobileMenuBtn}
                    <div class="card-actions">
                        ${downloadButtonHTML}
                        ${shareButtonHTML}
                        <button class="btn-action btn-rename" title="Rename" data-action="rename"><img src="static/assets/icons/edit.svg" alt="" width="16" height="16" class="action-icon"></button>
                        <button class="btn-action btn-delete" title="Delete" data-action="delete"><img src="static/assets/icons/trash.svg" alt="" width="16" height="16" class="action-icon"></button>
                    </div>
                `;
            } else if (canDownloadShared) {
                // Shared items with editor role: only download allowed
                actionsHTML = `
                    ${mobileMenuBtn}
                    <div class="card-actions">
                        ${downloadButtonHTML}
                    </div>
                `;
            }
            // Shared items with viewer role: no action buttons (actionsHTML stays empty)

            // For shared items, show who shared it
            const sharedByHTML = isSharedWithMe && item.shared_by_name ?
                `<span class="shared-by" title="${this.escapeHtml(item.shared_by_email || '')}">Shared by ${this.escapeHtml(item.shared_by_name)}</span>` : '';

            // Build card HTML with proper action buttons
            el.innerHTML = `
                <div class="file-icon">${icon}</div>
                <div class="file-info">
                    <div class="file-name" title="${escapedTitle}">${item.title}</div>
                    <div class="file-meta">
                        ${badgeHTML}
                        ${sizeStr ? `<span class="file-size">${sizeStr}</span>` : ''}
                        ${sharedByHTML}
                        ${dateStr ? `<span class="file-date">${dateStr}</span>` : ''}
                    </div>
                </div>
                ${actionsHTML}
            `;

            // Attach event handlers to action buttons
            const btnDownload = el.querySelector('.btn-download');
            const btnShare = el.querySelector('.btn-share');
            const btnRename = el.querySelector('.btn-rename');
            const btnDelete = el.querySelector('.btn-delete');

            if (btnDownload) {
                btnDownload.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Use file_id (UUID) with subpath when available, fallback to data_path for sessions
                    const downloadPath = item.file_id ? this.buildCaseRef(item) : (item.data_path || item.path);
                    this.downloadFolder(downloadPath, item.title, btnDownload);
                });
            }

            if (btnShare) {
                btnShare.onclick = (e) => {
                    e.stopPropagation();
                    this.openShareModal(item.id, item.type, item.title, item.file_id, item.file_id_sub_path);
                };
            }

            if (btnRename) {
                btnRename.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this.openRenameModal(item.path, item.title);
                });
            }

            if (btnDelete) {
                btnDelete.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this.openDeleteModal(item.path, item.title);
                });
            }

            // Mobile menu button handler
            const btnCardMenu = el.querySelector('.btn-card-menu');
            const cardActions = el.querySelector('.card-actions');
            if (btnCardMenu && cardActions) {
                btnCardMenu.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Close any other open menus first
                    document.querySelectorAll('.card-actions.show').forEach(menu => {
                        if (menu !== cardActions) menu.classList.remove('show');
                    });
                    cardActions.classList.toggle('show');
                });
            }

            this.grid.appendChild(el);
        });
    }

    // Format bytes to human readable
    formatBytes(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    // Browse into a subfolder
    async browseFolder(path) {
        try {
            const result = await this.openfoamLoader.browseDirectory(path);
            if (result.status === 'success') {
                const items = result.items || [];
                this.currentPath = path;

                // Store display names for breadcrumb
                items.forEach(item => {
                    if (item.internal_name && item.name) {
                        this.folderDisplayNames[item.internal_name] = item.name;
                    }
                });

                // Update breadcrumb using relative_path from result
                this.currentRelativePath = result.relative_path || '';
                this.renderBreadcrumb(this.currentRelativePath, result);

                this.renderGrid(items.map(item => ({
                    id: item.path,
                    title: item.name,
                    type: 'file',
                    is_dir: item.is_dir,
                    is_case: item.is_case,
                    case_type: item.case_type,
                    size: item.size || 0,
                    updated_at: item.modified || Date.now(),
                    is_shared: false,
                    path: item.path,
                    file_id: item.file_id || null,
                    file_id_sub_path: item.file_id_sub_path || '',
                    relativePath: item.relative_path || ''
                })));
            }
        } catch (e) {
            console.error('Browse folder error:', e);
        }
    }

    /**
     * Browse a shared folder (from "Shared With Me" view)
     * Uses the same browse API but with absolute path for shared content
     */
    async browseSharedFolder(path, sessionId) {
        console.log('browseSharedFolder called with path:', path, 'sessionId:', sessionId);
        try {
            // Ensure path starts with / for absolute path detection
            const absolutePath = path.startsWith('/') ? path : '/' + path;

            const result = await this.openfoamLoader.browseDirectory(absolutePath);
            console.log('browseDirectory result:', result);
            if (result.status === 'success') {
                const items = result.items || [];
                this.currentPath = absolutePath;
                this.currentSessionId = sessionId; // Track which session we're browsing

                // Store display names for breadcrumb
                items.forEach(item => {
                    if (item.internal_name && item.name) {
                        this.folderDisplayNames[item.internal_name] = item.name;
                    }
                });

                // Update breadcrumb for shared folder
                this.renderSharedBreadcrumb(result.relative_path || '', result, sessionId);

                this.renderGrid(items.map(item => ({
                    id: item.path,
                    title: item.name,
                    type: 'file',
                    is_dir: item.is_dir,
                    is_case: item.is_case,
                    case_type: item.case_type,
                    size: item.size || 0,
                    updated_at: item.modified || Date.now(),
                    is_shared: true, // Mark as shared content
                    path: item.path,
                    file_id: item.file_id || null,
                    file_id_sub_path: item.file_id_sub_path || '',
                    relativePath: item.relative_path || '',
                    sessionId: sessionId // Track parent session for navigation
                })));
            } else {
                console.error('Browse shared folder failed:', result.error);
                this.grid.innerHTML = `<div class="error">Failed to browse shared folder: ${result.error || 'Unknown error'}</div>`;
            }
        } catch (e) {
            console.error('Browse shared folder error:', e);
            this.grid.innerHTML = `<div class="error">Failed to browse shared folder: ${e.message}</div>`;
        }
    }

    /**
     * Render breadcrumb for shared folder navigation
     */
    renderSharedBreadcrumb(relativePath, result = {}, sessionId) {
        if (!this.breadcrumb) return;

        // Show breadcrumb
        this.breadcrumb.style.display = 'flex';

        let html = '';

        // Root item - back to "Shared With Me" list
        html += `<span class="breadcrumb-item" data-action="back-to-shared"><img src="static/assets/icons/users.svg" alt="" width="16" height="16" class="breadcrumb-icon"> Shared With Me</span>`;

        // Show the shared folder path
        const parts = (relativePath || '').split('/').filter(p => p);
        let cumulativePath = result.user_root || '';

        parts.forEach((part, index) => {
            cumulativePath += (cumulativePath.endsWith('/') ? '' : '/') + part;
            const isLast = index === parts.length - 1;
            const displayName = this.folderDisplayNames[part] || part;

            html += `<span class="breadcrumb-separator">/</span>`;
            if (isLast) {
                html += `<span class="breadcrumb-current">${displayName}</span>`;
            } else {
                html += `<span class="breadcrumb-item" data-path="${cumulativePath}" data-session="${sessionId}">${displayName}</span>`;
            }
        });

        this.breadcrumb.innerHTML = html;

        // Add click handlers
        this.breadcrumb.querySelectorAll('.breadcrumb-item').forEach(item => {
            item.addEventListener('click', () => {
                if (item.dataset.action === 'back-to-shared') {
                    // Go back to shared sessions list
                    this.filter = 'shared-with-me';
                    this.loadContent();
                } else {
                    const path = item.dataset.path;
                    const session = item.dataset.session;
                    if (path) {
                        this.browseSharedFolder(path, session);
                    }
                }
            });
        });
    }

    // Render breadcrumb navigation
    renderBreadcrumb(relativePath, result = {}) {
        if (!this.breadcrumb) return;

        // Show breadcrumb (may have been hidden for shared sessions)
        this.breadcrumb.style.display = 'flex';

        // relativePath is like "folder_uuid/subfolder" - only the user's folders
        const parts = (relativePath || '').split('/').filter(p => p);
        let html = '';

        // Root item - user's files (always shown)
        const userInfo = window.Auth ? window.Auth.getUserInfo() : null;
        const userDisplayName = userInfo?.name || 'My Files';
        html += `<span class="breadcrumb-item" data-path="" data-relative=""><img src="static/assets/icons/folder-case.svg" alt="" width="16" height="16" class="breadcrumb-icon"> ${userDisplayName}</span>`;

        // Build path segments from the relative path
        let cumulativeRelative = '';
        parts.forEach((part, index) => {
            cumulativeRelative += (cumulativeRelative ? '/' : '') + part;
            const isLast = index === parts.length - 1;

            // Use display name if available, otherwise use internal name
            const displayName = this.folderDisplayNames[part] || part;

            html += `<span class="breadcrumb-separator">/</span>`;
            if (isLast) {
                html += `<span class="breadcrumb-current">${displayName}</span>`;
            } else {
                // For intermediate paths, we need to construct the full path
                // The full path can be derived from user_root + relative path
                const fullPath = result.user_root ? `${result.user_root}/${cumulativeRelative}` : cumulativeRelative;
                html += `<span class="breadcrumb-item" data-path="${fullPath}" data-relative="${cumulativeRelative}">${displayName}</span>`;
            }
        });

        this.breadcrumb.innerHTML = html;

        // Add click handlers to breadcrumb items
        this.breadcrumb.querySelectorAll('.breadcrumb-item').forEach(item => {
            item.addEventListener('click', () => {
                const path = item.dataset.path;
                if (path === '') {
                    // Go to root
                    this.currentPath = null;
                    this.currentRelativePath = '';
                    this.loadMySimulations();
                } else {
                    this.browseFolder(path);
                }
            });
        });
    }

    async handleFolderUpload(e) {
        const files = e.target.files;
        if (files.length > 0) {
            const uploader = new FolderUploader(this); // Pass dashboard as viewer stub
            try {
                const result = await uploader.uploadFolder(files, null);
                if (result.status === 'success') {
                    // Fetch actual storage data before showing success modal
                    let storageUsed = 0;
                    let storageTotal = 0;
                    try {
                        const storageRes = await window.Auth.authenticatedFetch('/api/auth/storage');
                        const storageData = await storageRes.json();
                        if (storageData.status === 'success' && storageData.data) {
                            storageUsed = storageData.data.used_bytes || 0;
                            storageTotal = storageData.data.total_bytes || 0;
                        }
                    } catch (storageErr) {
                        console.error('Failed to fetch storage data:', storageErr);
                    }

                    // Hide progress modal before showing success modal
                    uploader.hideProgress();

                    // Show success modal with actual storage data
                    FolderUploader.showUploadSuccess({
                        caseName: result.data.case_name,
                        fileCount: result.data.file_count,
                        isOpenFOAM: result.data.is_openfoam,
                        storageUsed: storageUsed,
                        storageTotal: storageTotal
                    });

                    // Refresh list and storage display
                    this.loadContent();
                    this.updateStorageDisplay();
                }
            } catch (err) {
                console.error(err);
                uploader.hideProgress(); // Hide progress on error too
                Utils.notify("Upload failed: " + err.message, "error");
                FolderUploader.showUpgradePrompt(err.message); // If quota
            }
        }
    }

    // Setup rename modal event listeners
    setupRenameModal() {
        const renameModal = document.getElementById('renameModal');
        if (!renameModal) return;

        // Prevent duplicate event listener setup
        if (renameModal._listenersAttached) return;
        renameModal._listenersAttached = true;

        const closeBtn = document.getElementById('btnCloseRename');
        const cancelBtn = document.getElementById('btnCancelRename');
        const confirmBtn = document.getElementById('btnConfirmRename');
        const renameInput = document.getElementById('renameInput');

        // Close button
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeRenameModal());
        }

        // Cancel button
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.closeRenameModal());
        }

        // Confirm button
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => this.confirmRename());
        }

        // Enter key in input
        if (renameInput) {
            renameInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.confirmRename();
                } else if (e.key === 'Escape') {
                    this.closeRenameModal();
                }
            });
        }

        // Click outside to close
        renameModal.addEventListener('mousedown', (e) => {
            renameModal._clickedBackdrop = (e.target === renameModal);
        });
        renameModal.addEventListener('mouseup', (e) => {
            if (e.target === renameModal && renameModal._clickedBackdrop) {
                this.closeRenameModal();
            }
            renameModal._clickedBackdrop = false;
        });
    }

    // Open rename modal
    openRenameModal(path, currentName) {
        this.renameInfo = { path, currentName };

        const modal = document.getElementById('renameModal');
        const input = document.getElementById('renameInput');
        const title = modal.querySelector('.modal-header h3');

        if (title) {
            title.textContent = 'Rename';
        }

        if (input) {
            input.value = currentName || '';
        }

        // Show modal
        requestAnimationFrame(() => {
            modal.classList.add('active');
            // Focus and select all text
            if (input) {
                input.focus();
                input.select();
            }
        });
    }

    // Close rename modal
    closeRenameModal() {
        const modal = document.getElementById('renameModal');
        modal.classList.remove('active');
        this.renameInfo = null;
    }

    // Confirm rename action
    async confirmRename() {
        if (!this.renameInfo) return;

        const input = document.getElementById('renameInput');
        const newName = input ? input.value.trim() : '';

        if (!newName) {
            this.showNotification('Please enter a name', 'error');
            return;
        }

        if (newName === this.renameInfo.currentName) {
            this.closeRenameModal();
            return;
        }

        try {
            // Call rename API
            const res = await window.Auth.authenticatedFetch('/api/files/rename-by-path', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    file_path: this.renameInfo.path,
                    new_display_name: newName
                })
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                const errorMsg = errorData.detail || `Server error: ${res.status}`;
                throw new Error(errorMsg);
            }

            this.showNotification(`Renamed to "${newName}"`, 'success');
            this.closeRenameModal();
            // Refresh
            this.loadContent();

        } catch (e) {
            this.showNotification("Rename failed: " + e.message, 'error');
        }
    }

    // Legacy method - now opens modal
    renameItem(path, currentName) {
        this.openRenameModal(path, currentName);
    }

    // Setup delete modal event listeners
    setupDeleteModal() {
        const deleteModal = document.getElementById('deleteModal');
        if (!deleteModal) return;

        // Prevent duplicate event listener setup
        if (deleteModal._listenersAttached) return;
        deleteModal._listenersAttached = true;

        const closeBtn = document.getElementById('btnCloseDelete');
        const cancelBtn = document.getElementById('btnCancelDelete');
        const confirmBtn = document.getElementById('btnConfirmDelete');

        // Close button
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeDeleteModal());
        }

        // Cancel button
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.closeDeleteModal());
        }

        // Confirm button
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => this.confirmDelete());
        }

        // Click outside to close
        deleteModal.addEventListener('mousedown', (e) => {
            deleteModal._clickedBackdrop = (e.target === deleteModal);
        });
        deleteModal.addEventListener('mouseup', (e) => {
            if (e.target === deleteModal && deleteModal._clickedBackdrop) {
                this.closeDeleteModal();
            }
            deleteModal._clickedBackdrop = false;
        });
    }

    // Open delete modal
    openDeleteModal(path, name) {
        const displayName = name || path.split('/').pop();
        this.deleteInfo = { path, name: displayName };

        const modal = document.getElementById('deleteModal');
        const message = document.getElementById('deleteMessage');

        if (message) {
            message.textContent = `Are you sure you want to delete "${displayName}"?`;
        }

        // Show modal
        requestAnimationFrame(() => {
            modal.classList.add('active');
        });
    }

    // Close delete modal
    closeDeleteModal() {
        const modal = document.getElementById('deleteModal');
        modal.classList.remove('active');
        this.deleteInfo = null;
    }

    // Confirm delete action
    async confirmDelete() {
        if (!this.deleteInfo) return;
        if (this._deleteInProgress) return;

        // Prevent double-clicks - capture info, set flag, and disable button
        const { path, name } = this.deleteInfo;
        this.deleteInfo = null;
        this._deleteInProgress = true;

        const confirmBtn = document.getElementById('btnConfirmDelete');
        if (confirmBtn) confirmBtn.disabled = true;

        try {
            await this.openfoamLoader.deleteUpload(path);
            this.showNotification(`"${name}" deleted successfully`, 'success');
            this.closeDeleteModal();
            this.loadContent();
            this.updateStorageDisplay();
        } catch (e) {
            this.showNotification("Failed to delete: " + e.message, 'error');
            this.closeDeleteModal();
        } finally {
            this._deleteInProgress = false;
            if (confirmBtn) confirmBtn.disabled = false;
        }
    }

    // Legacy method - now opens modal
    deleteItem(path, name) {
        this.openDeleteModal(path, name);
    }

    // Download folder as ZIP or single file with progress indicator
    async downloadFolder(path, name, buttonElement = null) {
        // Use shared utility function (perimeter 95.4 for 28x28 dashboard buttons)
        await Utils.downloadFolderWithProgress(path, name, buttonElement, 95.4);
    }

    // Open share modal - delegates to shared ShareModal component
    openShareModal(id, type, name, file_id = null, file_id_sub_path = '') {
        if (window.shareModal) {
            window.shareModal.open(id, type, name, file_id, file_id_sub_path);
        }
    }

    // Show notification toast
    showNotification(message, type = 'info') {
        // Check if Utils has notify
        if (typeof Utils !== 'undefined' && Utils.notify) {
            Utils.notify(message, type);
        } else {
            // Fallback to alert for errors, console for others
            if (type === 'error') {
                alert(message);
            } else {
                console.log(`[${type}] ${message}`);
            }
        }
    }

    // Storage Methods (Copied/Adapted from Controls.js)
    async updateStorageDisplay() {
        try {
            const res = await window.Auth.authenticatedFetch('/api/auth/storage');
            const result = await res.json();

            if (result.status === 'success' && result.data) {
                const { used_bytes, total_bytes, used_percentage, quota_gb } = result.data;
                const progressBar = document.getElementById('storageProgressBar');
                const usageText = document.getElementById('storageUsageText');
                const usedAmount = document.getElementById('storageUsedAmount');
                const totalAmount = document.getElementById('storageTotalAmount');

                if (progressBar) {
                    progressBar.style.width = `${Math.min(used_percentage, 100)}%`;
                    // Color logic
                    if (used_percentage >= 90) progressBar.style.background = '#f44336';
                    else if (used_percentage >= 75) progressBar.style.background = '#ff9800';
                    else progressBar.style.background = '#2196F3';
                }
                if (usageText) usageText.textContent = `${used_percentage.toFixed(1)}% used`;
                if (usedAmount) usedAmount.textContent = Utils.formatBytes(used_bytes);
                if (totalAmount) totalAmount.textContent = `${quota_gb.toFixed(0)} GB`;
            }
        } catch (e) { console.error('Storage update failed', e); }
    }

    /**
     * Load CAD Designs View (Admin Only)
     */
    async loadDesignsView() {
        const token = window.Auth.getToken();
        if (!token) {
            Utils.notify('Please login to view CAD designs', 'warning');
            return;
        }

        try {
            // Hide tag filter initially
            const tagFilter = document.getElementById('designTagFilter');
            if (tagFilter) tagFilter.style.display = 'none';

            // Show loading state
            this.grid.innerHTML = '<div class="loading-spinner">Loading designs...</div>';

            const response = await fetch('/api/cad/documents', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Admin access required');
                }
                throw new Error('Failed to load designs');
            }

            const data = await response.json();
            this.renderDesignsGrid(data.documents || []);

            // Load popular tags if there are designs
            if (data.documents && data.documents.length > 0) {
                this.loadPopularTags();
            }
        } catch (error) {
            console.error('Failed to load CAD designs:', error);
            this.grid.innerHTML = `<div class="error-state">
                <p>${error.message}</p>
                ${error.message.includes('Admin') ? '<p style="font-size: 14px; color: #888;">Only administrators can access CAD designs.</p>' : ''}
            </div>`;
        }
    }

    /**
     * Render CAD designs as cards
     */
    renderDesignsGrid(designs) {
        this.grid.className = 'file-list-grid';
        this.grid.innerHTML = '';

        if (designs.length === 0) {
            this.grid.innerHTML = `<div class="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 16px; opacity: 0.3;">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M9 3v18M3 9h18M3 15h18M15 3v18"></path>
                </svg>
                <p>No CAD designs found.</p>
                <p style="font-size: 14px; color: #888;">Create your first design using the "Design" button above.</p>
            </div>`;
            return;
        }

        designs.forEach(design => {
            const el = document.createElement('div');
            el.className = 'file-item cad-design';
            el.dataset.id = design.id;
            el.dataset.type = 'cad-design';

            // Icon - same style as simulation cards
            const icon = '<img src="static/assets/icons/cube-3d.svg" alt="" width="48" height="48" class="file-icon-img">';

            // Badge - show first tag or "CAD Design"
            const tags = design.tags || [];
            const primaryTag = tags.length > 0 ? tags[0] : 'CAD Design';
            const badgeHTML = `<div class="case-badge cad-badge">${this.escapeHtml(primaryTag)}</div>`;

            // Additional tags (skip first one)
            const tagsHTML = tags.length > 1
                ? `<div class="design-tags">${tags.slice(1).map(t => `<span class="tag-pill">${this.escapeHtml(t)}</span>`).join('')}</div>`
                : '';

            // Date
            const dateStr = design.updated_at ? new Date(design.updated_at).toLocaleDateString() : '';

            // Action buttons - match simulation cards exactly
            const downloadButtonHTML = `
                <button class="btn-action btn-download" title="Download" data-action="download">
                    <img src="static/assets/icons/download.svg" alt="" width="16" height="16" class="action-icon">
                    <div class="download-progress">
                        <svg viewBox="0 0 28 28">
                            <rect class="progress-bg" x="1" y="1" width="26" height="26" rx="5" ry="5"></rect>
                            <rect class="progress-ring" x="1" y="1" width="26" height="26" rx="5" ry="5"></rect>
                        </svg>
                        <span class="progress-text">0%</span>
                    </div>
                </button>
            `;

            const actionsHTML = `
                <button class="btn-card-menu" title="Actions" aria-label="Actions">⋮</button>
                <div class="card-actions">
                    ${downloadButtonHTML}
                    <button class="btn-action btn-share" title="Share" data-action="share"><img src="static/assets/icons/share.svg" alt="" width="16" height="16" class="action-icon"></button>
                    <button class="btn-action btn-rename" title="Rename" data-action="rename"><img src="static/assets/icons/edit.svg" alt="" width="16" height="16" class="action-icon"></button>
                    <button class="btn-action btn-delete" title="Delete" data-action="delete"><img src="static/assets/icons/trash.svg" alt="" width="16" height="16" class="action-icon"></button>
                </div>
            `;

            // Card HTML
            el.innerHTML = `
                <div class="file-icon">${icon}</div>
                <div class="file-info">
                    <div class="file-name">${this.escapeHtml(design.name || 'Untitled')}</div>
                    <div class="file-meta">
                        ${badgeHTML}
                        <span class="file-date">${dateStr}</span>
                    </div>
                    ${tagsHTML}
                </div>
                ${actionsHTML}
            `;

            // Click handler - open design
            el.addEventListener('click', (e) => {
                if (!e.target.closest('.btn-action, .btn-card-menu')) {
                    window.location.href = `index.html?mode=cad&document=${design.id}`;
                }
            });

            // Action button menu toggle
            const menuBtn = el.querySelector('.btn-card-menu');
            const actionsMenu = el.querySelector('.card-actions');
            if (menuBtn && actionsMenu) {
                menuBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    actionsMenu.classList.toggle('show');
                });
            }

            this.grid.appendChild(el);
        });

        // Attach action handlers
        this.attachDesignActionHandlers();
    }

    /**
     * Attach action handlers to design cards
     */
    attachDesignActionHandlers() {
        document.querySelectorAll('.file-item[data-type="cad-design"]').forEach(card => {
            const id = card.dataset.id;
            const name = card.querySelector('.file-name').textContent;

            // Download - export as STEP file
            const downloadBtn = card.querySelector('[data-action="download"]');
            if (downloadBtn) {
                downloadBtn.addEventListener('click', async (e) => {
                    e.stopPropagation();

                    try {
                        const token = window.Auth.getToken();
                        const response = await fetch(`/api/cad/export/step/${id}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        if (!response.ok) {
                            throw new Error('Export failed');
                        }

                        // Download the file
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${name}.step`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);

                        Utils.notify('Design exported successfully', 'success');
                    } catch (error) {
                        console.error('Download failed:', error);
                        Utils.notify('Export not yet implemented', 'warning');
                    }
                });
            }

            // Share
            const shareBtn = card.querySelector('[data-action="share"]');
            if (shareBtn) {
                shareBtn.addEventListener('click', (e) => {
                    e.stopPropagation();

                    // Use existing share modal if available
                    if (window.shareModal) {
                        window.shareModal.show({
                            id: id,
                            name: name,
                            type: 'cad-design'
                        });
                    } else {
                        Utils.notify('Share functionality coming soon', 'info');
                    }
                });
            }

            // Rename
            const renameBtn = card.querySelector('[data-action="rename"]');
            if (renameBtn) {
                renameBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.showRenameDesignModal(id, name);
                });
            }

            // Delete
            const deleteBtn = card.querySelector('[data-action="delete"]');
            if (deleteBtn) {
                deleteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.showDeleteDesignModal(id, name);
                });
            }
        });
    }

    /**
     * Load popular tags for filtering
     */
    async loadPopularTags() {
        const token = window.Auth.getToken();
        if (!token) return;

        try {
            const response = await fetch('/api/cad/tags/popular?limit=10', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) return;

            const data = await response.json();
            const tagFilter = document.getElementById('designTagFilter');
            const tagPills = document.getElementById('tagPills');

            if (data.tags && data.tags.length > 0 && tagFilter && tagPills) {
                tagFilter.style.display = 'flex';
                tagPills.innerHTML = data.tags.map(tag =>
                    `<button class="tag-pill-filter" data-tag="${this.escapeHtml(tag)}">${this.escapeHtml(tag)}</button>`
                ).join('');

                // Add click handlers for tag filtering
                tagPills.querySelectorAll('.tag-pill-filter').forEach(pill => {
                    pill.addEventListener('click', () => this.filterDesignsByTag(pill.dataset.tag, pill));
                });
            }
        } catch (error) {
            console.error('Failed to load popular tags:', error);
        }
    }

    /**
     * Filter designs by tag
     */
    async filterDesignsByTag(tag, pillElement) {
        const token = window.Auth.getToken();
        if (!token) return;

        try {
            // Toggle active state
            const wasActive = pillElement.classList.contains('active');

            // Remove active from all pills
            document.querySelectorAll('.tag-pill-filter').forEach(p => p.classList.remove('active'));

            if (wasActive) {
                // Was active, now deactivate - show all designs
                this.loadDesignsView();
                return;
            }

            // Activate this pill
            pillElement.classList.add('active');

            // Show loading
            this.grid.innerHTML = '<div class="loading-spinner">Filtering...</div>';

            // Fetch filtered designs
            const response = await fetch(`/api/cad/documents?tags=${encodeURIComponent(tag)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Failed to filter designs');
            }

            const data = await response.json();
            this.renderDesignsGrid(data.documents || []);
        } catch (error) {
            console.error('Failed to filter designs:', error);
            Utils.notify('Failed to filter designs', 'error');
        }
    }

    /**
     * Show rename modal for design
     */
    showRenameDesignModal(documentId, currentName) {
        const modal = document.getElementById('renameModal');
        const input = document.getElementById('renameInput');
        const confirmBtn = document.getElementById('btnConfirmRename');

        if (!modal || !input || !confirmBtn) return;

        input.value = currentName;
        modal.style.display = 'flex';
        input.focus();
        input.select();

        // Remove old listeners
        const newConfirmBtn = confirmBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);

        // Add new listener
        newConfirmBtn.addEventListener('click', async () => {
            const newName = input.value.trim();
            if (!newName) {
                Utils.notify('Please enter a name', 'warning');
                return;
            }

            try {
                const token = window.Auth.getToken();
                const response = await fetch(`/api/cad/documents/${documentId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ name: newName })
                });

                if (!response.ok) {
                    throw new Error('Failed to rename design');
                }

                modal.style.display = 'none';
                Utils.notify('Design renamed successfully', 'success');
                this.loadDesignsView(); // Reload
            } catch (error) {
                console.error('Failed to rename design:', error);
                Utils.notify('Failed to rename design', 'error');
            }
        });
    }

    /**
     * Show delete modal for design
     */
    showDeleteDesignModal(documentId, designName) {
        const modal = document.getElementById('deleteModal');
        const message = document.getElementById('deleteMessage');
        const confirmBtn = document.getElementById('btnConfirmDelete');

        if (!modal || !message || !confirmBtn) return;

        message.textContent = `Are you sure you want to delete "${designName}"?`;
        modal.style.display = 'flex';

        // Remove old listeners
        const newConfirmBtn = confirmBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);

        // Add new listener
        newConfirmBtn.addEventListener('click', async () => {
            try {
                const token = window.Auth.getToken();
                const response = await fetch(`/api/cad/documents/${documentId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    throw new Error('Failed to delete design');
                }

                modal.style.display = 'none';
                Utils.notify('Design deleted successfully', 'success');
                this.loadDesignsView(); // Reload
            } catch (error) {
                console.error('Failed to delete design:', error);
                Utils.notify('Failed to delete design', 'error');
            }
        });
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
    }
}

// Initializer
document.addEventListener('DOMContentLoaded', () => {
    window.dashboard = new Dashboard();

    // Mobile menu toggle
    const mobileMenuToggle = document.getElementById('mobileMenuToggle');
    const mobileOverlay = document.getElementById('mobileOverlay');
    const sidebar = document.querySelector('.sidebar');

    if (mobileMenuToggle && sidebar) {
        const toggleMenu = () => {
            const isOpen = sidebar.classList.toggle('open');
            mobileMenuToggle.classList.toggle('active', isOpen);
            if (mobileOverlay) {
                mobileOverlay.classList.toggle('visible', isOpen);
            }
        };

        const closeMenu = () => {
            sidebar.classList.remove('open');
            mobileMenuToggle.classList.remove('active');
            if (mobileOverlay) {
                mobileOverlay.classList.remove('visible');
            }
        };

        mobileMenuToggle.addEventListener('click', toggleMenu);

        if (mobileOverlay) {
            mobileOverlay.addEventListener('click', closeMenu);
        }

        // Close menu when clicking a nav item
        sidebar.querySelectorAll('.nav-item').forEach(item => {
            item.addEventListener('click', () => {
                if (window.innerWidth <= 768) {
                    closeMenu();
                }
            });
        });

        // Close menu on window resize if becoming desktop
        window.addEventListener('resize', () => {
            if (window.innerWidth > 768) {
                closeMenu();
            }
        });
    }

    // Close card action menus when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.btn-card-menu') && !e.target.closest('.card-actions')) {
            document.querySelectorAll('.card-actions.show').forEach(menu => {
                menu.classList.remove('show');
            });
        }
    });
});
